/**
 * Commercial Performance — Overview tab.
 *
 * Asset-level commercial snapshot for a single DUID:
 *   - KPI strip      : Price captured · Revenue · Generation · Econ-curtailment · Grid-curtailment
 *   - Dispatch chart : MW output (left axis) + RRP (right axis), bucketed by period
 *   - Curtailment   : "could have produced vs grid-delivered vs lost-to-X" stacked bar
 *   - Capture vs benchmark : asset capture price vs regional RRP, period-buckets
 *
 * Reuses the existing `/api/portfolios/<id>/overview` (with ?asset_id=DUID)
 * and `/api/portfolios/<id>/dispatch` endpoints — both already accept the
 * asset filter and the period window. No new backend code required.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Card, Stack, Skeleton, Alert, IconButton,
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import RefreshIcon from '@mui/icons-material/Refresh';
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { useItemTooltip, useAxesTooltip, ChartsTooltipContainer } from '@mui/x-charts/ChartsTooltip';
import { useXAxis, useYAxis } from '@mui/x-charts/hooks';
import KpiCard, {
  fmtAUDCompact, fmtMWhCompact, fmtAUDPerMWh,
} from './KpiCard';
import { useThemeColors } from '../theme/ThemeModeContext';
import PeriodPicker, { defaultPeriodValue } from './PeriodPicker';
import AssetPicker from './AssetPicker';
import CommercialWorkspaceTabs from './CommercialWorkspaceTabs';
import { cachedGet } from '../utils/cachedFetch';
import { periodApiParams } from '../utils/periodParams';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

// Bids & offers card — REMOVED at the 2026-06-10 METIS review ("it doesn't
// show any useful data at this stage… no one actually asked for it"). The
// /bids endpoint and atlas.bid_stack still exist; the longer-term plan is a
// dedicated bidding-strategy/modelling module, not a card on this page.
// The card lived here until 5dce5b0^ if it ever needs resurrecting.

// Curtailment cause palette (matches the curtailment ribbon on Portfolio Overview)
const CAUSE_COLOR = {
  'Network':       '#3b82f6',
  'Thermal limit': '#f59e0b',
  'Neg. price':    '#ef4444',
  'Frequency':     '#8b5cf6',
  'Other':         '#6b7280',
};

// Custom Potential layer for the Production split chart — short horizontal
// dotted segments per bucket at the asset's potential MWh. Same pattern as
// FacilityCovenants' ForecastTicks (memory: forecast_discrete_marks) so
// "what could have been" reads as a discrete target per bucket rather than
// a connected line. Pair with the realized stack below to communicate the
// gap between potential and delivered without doubling the bar width.
const POTENTIAL_COLOR = '#64748b';   // slate — neutral, reads as target
function PotentialTicks({ data, color = POTENTIAL_COLOR }) {
  const xAxis = useXAxis();
  const yAxis = useYAxis();
  if (!xAxis?.scale || !yAxis?.scale || !data) return null;
  const xScale = xAxis.scale;
  const yScale = yAxis.scale;
  const bandWidth = typeof xScale.bandwidth === 'function' ? xScale.bandwidth() : 0;
  if (!bandWidth) return null;
  const half = bandWidth * 0.4;
  return (
    <g pointerEvents="none">
      {data.map((v, i) => {
        if (v == null) return null;
        const label = xAxis.data?.[i];
        const x = xScale(label);
        const y = yScale(v);
        if (x == null || y == null) return null;
        const cx = x + bandWidth / 2;
        return (
          <line
            key={i}
            x1={cx - half} x2={cx + half} y1={y} y2={y}
            stroke={color} strokeWidth={3}
            strokeDasharray="2 3" strokeLinecap="round"
          />
        );
      })}
    </g>
  );
}

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Sum a single curtailment cause from data.curtailment.split.
// "Economic" = Neg. price; "Grid" = Network. Anything else is bucketed under
// thermal / frequency / other and shown only on the chart, not as a KPI.
function sumCause(curtailment, cause) {
  const row = (curtailment?.split || []).find((s) => s.cause === cause);
  return row ? row.mwh : 0;
}


// ─── BatteryRevenueStackCard ─────────────────────────────────────────────
// Industry-standard layout for BESS revenue attribution:
//   • 100%-stacked horizontal ribbon at the top — Energy / Raise FCAS / Lower
//     FCAS gestalt at a glance.
//   • Sortable component table underneath — Service · Category · Enablement
//     (MWh) · Revenue (AUD) · $/MWh · Share %.
//
// The $/MWh column ("unit economics") is the diagnostic metric analysts
// actually optimise against — revenue alone hides whether a service was
// big because it paid well or because it was enabled a lot.

function compColor(key) {
  if (key === 'ENERGY')            return '#D98A2B';
  if (key?.startsWith('RAISEREG')) return '#E9B065';
  if (key?.startsWith('LOWERREG')) return '#3b82f6';
  if (key?.startsWith('RAISE'))    return '#a78bfa';
  if (key?.startsWith('LOWER'))    return '#60a5fa';
  return '#6b7280';
}

function compCategoryLabel(c) {
  if (c.category) return c.category;
  if (c.key === 'ENERGY')              return 'energy';
  if (c.key?.startsWith('RAISE'))      return 'raise';
  if (c.key?.startsWith('LOWER'))      return 'lower';
  return '—';
}

// ─── AssetManagerView ────────────────────────────────────────────────────
// The fund-manager scorecard: $/MW/day revenue intensity + Pareto
// concentration of revenue across services. Rendered alongside the KPI
// strip so users see the LP-grade summary up top, with the analyst grid
// (Revenue components) lower on the page for drilling.
// ─── RevenueInsightsCard ─────────────────────────────────────────────────
// Asset-manager-grade insights pulled from data we already fetch:
//
//   1. Best / Worst bucket of revenue   — how lumpy is the period?
//   2. Pareto on time buckets           — top X% of intervals drive Y% of $
//   3. Cumulative revenue vs prior      — pacing line chart
//   4. TB1 / TB2 spreads (battery only) — canonical BESS Modo benchmark
//
// All four sit in one card so the page doesn't sprawl. The "lumpy"
// question and the "are we on track" question are answered together —
// the same way an LP review would walk through them.
function RevenueInsightsCard({
  revenueSeries, priorRevenueSeries, dispatchSummary,
  isBattery, capacityMw, colors, period, loading,
}) {
  // `dispatchSummary` carries server-computed stats:
  //   tb1_aud_per_mwh, tb2_aud_per_mwh, region_avg_rrp_aud_per_mwh,
  //   capture_aud_per_mwh, capture_premium_aud_per_mwh, capture_rate_pct,
  //   negative_hours, negative_share_pct, total_hours.
  // Previously the component derived TB1/TB2 from a raw RRP series; now
  // backend (modules/portfolio/overview.py:_dispatch_summary) is the source.
  const total = revenueSeries.reduce((s, r) => s + (r.actual_aud ?? r.revenue_aud ?? 0), 0);

  // Best / Worst single bucket. Tie-breaker: earliest date.
  let best = null, worst = null;
  for (const r of revenueSeries) {
    const v = r.actual_aud ?? r.revenue_aud ?? 0;
    if (best  == null || v > (best.actual_aud  ?? best.revenue_aud  ?? 0)) best  = r;
    if (worst == null || v < (worst.actual_aud ?? worst.revenue_aud ?? 0)) worst = r;
  }

  // Pareto over buckets — sort by $ desc, walk accumulating %, find when
  // we cross 80%.
  const sorted = [...revenueSeries]
    .map((r) => ({ ...r, _aud: r.actual_aud ?? r.revenue_aud ?? 0 }))
    .filter((r) => r._aud > 0)
    .sort((a, b) => b._aud - a._aud);
  let running = 0;
  const annotated = sorted.map((r) => {
    running += r._aud;
    return { ...r, cumPct: total > 0 ? (running / total) * 100 : 0 };
  });
  const top80Idx = annotated.findIndex((r) => r.cumPct >= 80);
  const top80N   = top80Idx >= 0 ? top80Idx + 1 : annotated.length;
  const top10Pct = annotated[Math.max(0, Math.ceil(annotated.length * 0.1) - 1)]?.cumPct;

  // Cumulative arrays for current + prior trend line.
  const curXs    = revenueSeries.map((r) => new Date(r.date));
  let cumAcc = 0;
  const curCum   = revenueSeries.map((r) => {
    cumAcc += r.actual_aud ?? r.revenue_aud ?? 0;
    return cumAcc;
  });
  let priorAcc = 0;
  const priorCum = priorRevenueSeries.map((r) => {
    priorAcc += r.actual_aud ?? r.revenue_aud ?? 0;
    return priorAcc;
  });
  // Align the prior series to the same x-axis indices so the two lines
  // share an x-scale for visual comparison even though the calendar
  // dates differ.
  const priorAligned = curXs.map((_, i) => priorCum[i] ?? null);

  // TB1 / TB2 — daily top-hour vs bottom-hour RRP averages. Server-computed
  // (see modules/portfolio/overview.py:_dispatch_summary). Only meaningful
  // for batteries on sub-hourly buckets; backend returns null otherwise.
  const tb1 = isBattery ? (dispatchSummary?.tb1_aud_per_mwh ?? null) : null;
  const tb2 = isBattery ? (dispatchSummary?.tb2_aud_per_mwh ?? null) : null;

  const fmtBucketDate = (iso) => {
    if (!iso) return '—';
    // NEM-clock-as-UTC bucket timestamps — render in UTC so the date can't
    // roll backwards in negative-offset browser timezones.
    return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  };
  const _ignored = period;   // currently unused; kept in API for future header use

  return (
    <Card sx={{ p: 3, mb: 3 }}>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          Revenue insights
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          Concentration · pacing · spreads
        </Typography>
      </Stack>

      {loading ? (
        <Skeleton variant="rectangular" height={280} sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
      ) : !revenueSeries.length ? (
        <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="text.disabled">No revenue data in this period.</Typography>
        </Box>
      ) : (
        <Box sx={{
          display: 'grid', gap: 2,
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
        }}>
          {/* LEFT: Best/Worst day + Pareto narrative */}
          <Box sx={{
            border: '1px solid', borderColor: colors.hairline2,
            borderRadius: 1.5, p: 2,
            display: 'flex', flexDirection: 'column', gap: 1.75,
          }}>
            <Typography sx={{
              fontSize: '0.7rem', fontWeight: 600, color: 'text.disabled',
              letterSpacing: '0.06em', textTransform: 'uppercase',
            }}>
              Concentration & extremes
            </Typography>

            {/* Best / Worst chips */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              {best && (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>Best bucket</Typography>
                  <Typography sx={{
                    fontSize: '1.05rem', fontWeight: 700, color: '#22c55e',
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    {fmtAUDCompact(best.actual_aud ?? best.revenue_aud ?? 0)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                    {fmtBucketDate(best.date)}
                  </Typography>
                </Box>
              )}
              {worst && (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>Worst bucket</Typography>
                  <Typography sx={{
                    fontSize: '1.05rem', fontWeight: 700, color: '#ef4444',
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    {fmtAUDCompact(worst.actual_aud ?? worst.revenue_aud ?? 0)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                    {fmtBucketDate(worst.date)}
                  </Typography>
                </Box>
              )}
            </Stack>

            {/* Pareto narrative */}
            <Box>
              <Typography sx={{
                fontSize: '0.95rem', color: 'text.primary', lineHeight: 1.4,
              }}>
                <Box component="span" sx={{ fontWeight: 700 }}>{top80N}</Box>{' '}
                <Box component="span" sx={{ color: 'text.secondary' }}>of {annotated.length} buckets drive</Box>{' '}
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {(annotated[top80N - 1]?.cumPct ?? 0).toFixed(0)}%
                </Box>{' '}
                <Box component="span" sx={{ color: 'text.secondary' }}>of revenue.</Box>
              </Typography>
              {top10Pct != null && (
                <Typography sx={{ fontSize: '0.8rem', color: 'text.disabled', mt: 0.5 }}>
                  Top 10% of buckets = {top10Pct.toFixed(0)}% of revenue
                </Typography>
              )}
              {/* Pareto bar with amber 80% marker */}
              <Box sx={{ position: 'relative', mt: 1.25 }}>
                <Box sx={{
                  height: 8, borderRadius: 1, overflow: 'hidden',
                  display: 'flex', bgcolor: colors.track,
                }}>
                  {annotated.map((r, i) => (
                    <Box
                      key={i}
                      title={`${fmtBucketDate(r.date)}: ${fmtAUDCompact(r._aud)} (cum ${r.cumPct.toFixed(0)}%)`}
                      sx={{
                        width: `${total > 0 ? (r._aud / total) * 100 : 0}%`,
                        bgcolor: i < top80N ? '#3b82f6' : colors.hairline2,
                        borderRight: '1px solid', borderColor: 'background.paper',
                        '&:last-child': { borderRight: 'none' },
                      }}
                    />
                  ))}
                </Box>
                <Box sx={{
                  position: 'absolute', top: -2, bottom: -2, left: '80%',
                  width: 2, bgcolor: '#f59e0b', borderRadius: 1, pointerEvents: 'none',
                }} />
                <Typography sx={{
                  position: 'absolute', top: 12, left: 'calc(80% - 18px)',
                  fontSize: '0.62rem', color: '#f59e0b', fontWeight: 600,
                }}>
                  80%
                </Typography>
              </Box>
            </Box>

            {/* TB1/TB2 spreads — battery only */}
            {isBattery && tb1 != null && (
              <Box sx={{ pt: 1.25, borderTop: '1px dashed', borderColor: colors.hairline }}>
                <Typography sx={{
                  fontSize: '0.7rem', fontWeight: 600, color: 'text.disabled',
                  letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.75,
                }}>
                  Daily spread (Modo TB1 / TB2 avg)
                </Typography>
                <Stack direction="row" spacing={3}>
                  <Box>
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>TB1 · top vs bottom hour</Typography>
                    <Typography sx={{
                      fontSize: '1.1rem', fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums', color: 'text.primary',
                    }}>
                      A${tb1.toFixed(0)}<Box component="span" sx={{
                        fontSize: '0.78rem', color: 'text.secondary', fontWeight: 500, ml: 0.4,
                      }}>/MWh</Box>
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>TB2 · top vs bottom 2h</Typography>
                    <Typography sx={{
                      fontSize: '1.1rem', fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums', color: 'text.primary',
                    }}>
                      A${tb2.toFixed(0)}<Box component="span" sx={{
                        fontSize: '0.78rem', color: 'text.secondary', fontWeight: 500, ml: 0.4,
                      }}>/MWh</Box>
                    </Typography>
                  </Box>
                </Stack>
                {capacityMw > 0 && (
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', mt: 0.5 }}>
                    Per MW of capacity, theoretical TB2 daily revenue ≈ ${(tb2 * 2).toFixed(0)}/MW/day
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* RIGHT: Cumulative revenue with prior overlay */}
          <Box sx={{
            border: '1px solid', borderColor: colors.hairline2,
            borderRadius: 1.5, p: 2,
            display: 'flex', flexDirection: 'column', gap: 1,
          }}>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between">
              <Typography sx={{
                fontSize: '0.7rem', fontWeight: 600, color: 'text.disabled',
                letterSpacing: '0.06em', textTransform: 'uppercase',
              }}>
                Cumulative revenue
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled' }}>
                Current vs prior window
              </Typography>
            </Stack>
            <Box sx={{ flexGrow: 1, minHeight: 220 }}>
              <LineChart
                height={220}
                xAxis={[{
                  data: curXs,
                  scaleType: 'time',
                  valueFormatter: (v) => {
                    const d = v instanceof Date ? v : new Date(v);
                    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' });
                  },
                  tickLabelStyle: { fontSize: 11, fill: colors.textDim },
                }]}
                yAxis={[{
                  valueFormatter: (v) => fmtAUDCompact(v),
                  tickLabelStyle: { fontSize: 11, fill: colors.textDim },
                }]}
                series={[
                  { data: curCum, label: 'Current', color: '#3b82f6',
                    area: true, showMark: false,
                    valueFormatter: (v) => v == null ? '—' : fmtAUDCompact(v) },
                  ...(priorAligned.some((v) => v != null) ? [{
                    data: priorAligned, label: 'Prior window', color: colors.textDim,
                    showMark: false, connectNulls: true,
                    valueFormatter: (v) => v == null ? '—' : fmtAUDCompact(v),
                  }] : []),
                ]}
                margin={{ left: 64, right: 16, top: 16, bottom: 30 }}
                slotProps={{ legend: { position: { vertical: 'top', horizontal: 'right' } } }}
                sx={{
                  '& .MuiChartsAxis-line': { stroke: colors.hairline2 },
                  '& .MuiChartsAxis-tick':  { stroke: colors.hairline2 },
                  '& .MuiChartsGrid-line':  { stroke: colors.hairline },
                  '& .MuiAreaElement-root': { fillOpacity: 0.12 },
                  '& .MuiChartsLegend-label': { fill: colors.textDim, fontSize: '0.72rem' },
                }}
              />
            </Box>
          </Box>
        </Box>
      )}
    </Card>
  );
}


function AssetManagerView({ components, capacityMw, periodDays, colors }) {
  const totalRev = components.reduce((s, c) => s + (c.aud || 0), 0);
  const ratePerMWDay =
    capacityMw && capacityMw > 0 && periodDays && periodDays > 0
      ? totalRev / capacityMw / periodDays
      : null;
  const ratePerMWYear = ratePerMWDay != null ? ratePerMWDay * 365 : null;

  // Pareto — sort by $, walk down accumulating %, count how many cross 80%.
  const sorted = [...components]
    .filter((c) => (c.aud || 0) > 0)
    .sort((a, b) => (b.aud || 0) - (a.aud || 0));
  let running = 0;
  const annotated = sorted.map((c) => {
    running += (c.aud || 0);
    return { ...c, cumPct: totalRev > 0 ? (running / totalRev) * 100 : 0 };
  });
  const pareto80 = annotated.findIndex((c) => c.cumPct >= 80);
  const top80Count = pareto80 >= 0 ? pareto80 + 1 : annotated.length;
  const top80Names = annotated.slice(0, top80Count).map((c) => c.label);

  return (
    <Box sx={{
      display: 'grid', gap: 2,
      gridTemplateColumns: { xs: '1fr', md: '260px 1fr' },
      alignItems: 'stretch',
    }}>
        {/* Headline rate KPI */}
        <Box sx={{
          border: '1px solid', borderColor: colors.hairline2,
          borderRadius: 1.5, p: 2,
          display: 'flex', flexDirection: 'column', gap: 0.5,
        }}>
          <Typography sx={{
            fontSize: '0.7rem', fontWeight: 600,
            color: 'text.disabled',
            letterSpacing: '0.06em', textTransform: 'uppercase',
          }}>
            Revenue intensity
          </Typography>
          <Typography sx={{
            fontSize: '1.8rem', fontWeight: 700, lineHeight: 1.1,
            fontVariantNumeric: 'tabular-nums',
            color: ratePerMWDay != null ? 'text.primary' : 'text.disabled',
          }}>
            {ratePerMWDay != null ? `A$${ratePerMWDay.toFixed(0)}` : '—'}
            <Box component="span" sx={{
              fontSize: '0.85rem', fontWeight: 500,
              color: 'text.secondary', ml: 0.5,
            }}>
              / MW / day
            </Box>
          </Typography>
          <Typography sx={{
            fontSize: '0.75rem', color: 'text.disabled',
            fontVariantNumeric: 'tabular-nums',
          }}>
            {ratePerMWYear != null
              ? `Annualised: ${fmtAUDCompact(ratePerMWYear)} / MW / year`
              : `Awaiting asset capacity + period data`}
          </Typography>
          {capacityMw && periodDays ? (
            <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', mt: 0.5 }}>
              {fmtAUDCompact(totalRev)} ÷ {capacityMw} MW ÷ {periodDays} d
            </Typography>
          ) : null}
        </Box>

        {/* Pareto callout — visual concentration */}
        <Box sx={{
          border: '1px solid', borderColor: colors.hairline2,
          borderRadius: 1.5, p: 2,
        }}>
          <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.25 }}>
            <Typography sx={{
              fontSize: '0.7rem', fontWeight: 600,
              color: 'text.disabled',
              letterSpacing: '0.06em', textTransform: 'uppercase',
            }}>
              Revenue concentration (Pareto)
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>
              Top {top80Count} of {annotated.length} services
            </Typography>
          </Stack>
          {annotated.length === 0 ? (
            <Typography color="text.disabled" sx={{ fontSize: '0.85rem' }}>
              No revenue components.
            </Typography>
          ) : (
            <>
              <Typography sx={{
                fontSize: '0.95rem', color: 'text.primary',
                mb: 1.25, lineHeight: 1.4,
              }}>
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {top80Count}
                </Box>{' '}
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  service{top80Count === 1 ? '' : 's'} drive
                </Box>{' '}
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {(annotated[top80Count - 1]?.cumPct ?? 0).toFixed(0)}%
                </Box>{' '}
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  of revenue:
                </Box>{' '}
                <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  {top80Names.join(', ')}
                </Box>
              </Typography>

              <Box sx={{ position: 'relative' }}>
                <Box sx={{
                  height: 10, borderRadius: 1, overflow: 'hidden',
                  display: 'flex', bgcolor: colors.track,
                }}>
                  {annotated.map((c) => (
                    <Box
                      key={c.key}
                      title={`${c.label}: ${fmtAUDCompact(c.aud)} · ${(c.pct || 0).toFixed(1)}% (cum ${c.cumPct.toFixed(0)}%)`}
                      sx={{
                        width: `${c.pct || 0}%`,
                        bgcolor: compColor(c.key),
                        borderRight: '1px solid',
                        borderColor: 'background.paper',
                        '&:last-child': { borderRight: 'none' },
                      }}
                    />
                  ))}
                </Box>
                {/* 80% marker */}
                <Box sx={{
                  position: 'absolute', top: -2, bottom: -2,
                  left: '80%',
                  width: 2, bgcolor: '#f59e0b',
                  borderRadius: 1, pointerEvents: 'none',
                }} />
                <Typography sx={{
                  position: 'absolute', top: 14, left: 'calc(80% - 18px)',
                  fontSize: '0.65rem', color: '#f59e0b',
                  fontWeight: 600,
                }}>
                  80%
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>
  );
}


function BatteryRevenueStackCard({ components, loading, colors }) {
  const [sortField, setSortField] = useState('aud');
  const [sortDir,   setSortDir]   = useState('desc');

  // Decorate each row with the derived $/MWh "unit economics" column.
  // Energy arbitrage doesn't have an MWh-enabled figure (it's traded volume,
  // not capacity offered), so its $/MWh stays null and renders as "—".
  const rows = useMemo(() => {
    const out = (components || []).map((c) => ({
      ...c,
      category: compCategoryLabel(c),
      aud_per_mwh: c.mwh && c.mwh > 0 ? (c.aud || 0) / c.mwh : null,
    }));
    out.sort((a, b) => {
      const sign = sortDir === 'asc' ? 1 : -1;
      const av = a[sortField];
      const bv = b[sortField];
      // Push nulls to the bottom regardless of sort direction
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') return sign * av.localeCompare(bv);
      return sign * (av - bv);
    });
    return out;
  }, [components, sortField, sortDir]);

  const total = components.reduce((s, c) => s + (c.aud || 0), 0);
  const energyTotal = components.filter((c) => compCategoryLabel(c) === 'energy')
                                .reduce((s, c) => s + (c.aud || 0), 0);
  const raiseTotal  = components.filter((c) => compCategoryLabel(c) === 'raise')
                                .reduce((s, c) => s + (c.aud || 0), 0);
  const lowerTotal  = components.filter((c) => compCategoryLabel(c) === 'lower')
                                .reduce((s, c) => s + (c.aud || 0), 0);
  const pctOf = (v) => total > 0 ? (v / total) * 100 : 0;

  // Click handler for column headers — toggles direction if same column,
  // otherwise picks the new field with a sensible default direction
  // (descending for numeric columns, ascending for text columns).
  const onSort = (field, isNumeric = true) => {
    if (field === sortField) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir(isNumeric ? 'desc' : 'asc');
    }
  };

  const SortableHeader = ({ field, label, align = 'left', numeric = true }) => {
    const active = sortField === field;
    return (
      <Typography
        onClick={() => onSort(field, numeric)}
        sx={{
          fontSize: '0.7rem', fontWeight: 600,
          color: active ? 'primary.main' : 'text.disabled',
          letterSpacing: '0.06em', textTransform: 'uppercase',
          cursor: 'pointer', userSelect: 'none',
          textAlign: align, whiteSpace: 'nowrap',
          '&:hover': { color: 'text.secondary' },
        }}
      >
        {label}{active && (sortDir === 'asc' ? ' ↑' : ' ↓')}
      </Typography>
    );
  };

  return (
    <Card sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.5 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          Revenue components
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>
          Total {fmtAUDCompact(total)}
        </Typography>
      </Stack>

      {loading ? (
        <>
          <Skeleton variant="rectangular" height={28} sx={{ bgcolor: colors.skeleton, borderRadius: 1, mb: 2 }} />
          <Stack spacing={0.8}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} variant="rectangular" height={28}
                        sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
            ))}
          </Stack>
        </>
      ) : !components.length ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography color="text.disabled">No revenue components for this battery.</Typography>
        </Box>
      ) : (
        <>
          {/* 100%-stacked ribbon — Energy / Raise / Lower categorical split */}
          <Box sx={{ mb: 1 }}>
            <Box sx={{
              height: 18, borderRadius: 1, overflow: 'hidden',
              display: 'flex', bgcolor: colors.track,
            }}>
              {components
                .filter((c) => (c.aud || 0) > 0)
                .map((c) => (
                  <Box
                    key={c.key}
                    title={`${c.label}: ${fmtAUDCompact(c.aud)} (${(c.pct || 0).toFixed(1)}%)`}
                    sx={{
                      width: `${c.pct || 0}%`,
                      bgcolor: compColor(c.key),
                      borderRight: '1px solid',
                      borderColor: 'background.paper',
                      '&:last-child': { borderRight: 'none' },
                      transition: 'opacity 120ms',
                      '&:hover': { opacity: 0.85 },
                    }}
                  />
                ))}
            </Box>
            <Stack direction="row" spacing={2.5} sx={{ mt: 0.9, mb: 1.5 }}>
              {[
                { label: 'Energy', value: energyTotal, color: '#D98A2B' },
                { label: 'Raise FCAS', value: raiseTotal, color: '#E9B065' },
                { label: 'Lower FCAS', value: lowerTotal, color: '#3b82f6' },
              ].map((g) => (
                <Stack key={g.label} direction="row" alignItems="center" spacing={0.75}>
                  <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: g.color }} />
                  <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                    {g.label}
                  </Typography>
                  <Typography sx={{
                    fontSize: '0.78rem', fontWeight: 600, color: 'text.primary',
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    {fmtAUDCompact(g.value)}
                  </Typography>
                  <Typography sx={{
                    fontSize: '0.7rem', color: 'text.disabled',
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    {pctOf(g.value).toFixed(0)}%
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>

          {/* Sortable detail table. Each header is a clickable Typography that
              toggles sort direction. Click order: Service · Category · Enablement
              · Revenue · $/MWh · Share. Default = Revenue desc. */}
          <Box sx={{
            display: 'grid',
            // 7 columns now — the trailing one is a per-row horizontal bar
            // ("chart in the grid"), sized to its largest sibling's value so
            // the relative magnitudes read at a glance.
            gridTemplateColumns: '2fr 0.8fr 1fr 1fr 1fr 0.7fr 1.6fr',
            columnGap: 1.5, rowGap: 0,
            alignItems: 'center',
          }}>
            {/* Header row */}
            <SortableHeader field="label"       label="Service"          numeric={false} />
            <SortableHeader field="category"    label="Category"         numeric={false} />
            <SortableHeader field="mwh"         label="Enablement (MWh)" align="right" />
            <SortableHeader field="aud"         label="Revenue (AUD)"    align="right" />
            <SortableHeader field="aud_per_mwh" label="A$/MWh"            align="right" />
            <SortableHeader field="pct"         label="Share"            align="right" />
            <Typography sx={{
              fontSize: '0.7rem', fontWeight: 600,
              color: 'text.disabled',
              letterSpacing: '0.06em', textTransform: 'uppercase',
              userSelect: 'none', whiteSpace: 'nowrap',
            }}>
              Revenue bar
            </Typography>
            {/* Divider under header */}
            <Box sx={{
              gridColumn: '1 / -1', height: '1px',
              bgcolor: colors.hairline2, mt: 0.6, mb: 0.6,
            }} />
            {/* Data rows.
                The trailing "Revenue bar" cell is the in-grid chart — width
                is scaled to the largest absolute revenue across all rows
                (not capped at 100%) so the longest bar always fills the
                column and shorter ones read proportionally. */}
            {(() => {
              const maxAbs = rows.reduce((m, r) => Math.max(m, Math.abs(r.aud || 0)), 1);
              return rows.map((c) => {
                const color = compColor(c.key);
                const isNeg = (c.aud || 0) < 0;
                const barPct = (Math.abs(c.aud || 0) / maxAbs) * 100;
                return (
                  <React.Fragment key={c.key}>
                    <Stack direction="row" alignItems="center" spacing={0.85} sx={{ py: 0.85, minWidth: 0 }}>
                      <Box sx={{
                        width: 9, height: 9, borderRadius: '50%', bgcolor: color, flexShrink: 0,
                      }} />
                      <Typography sx={{
                        fontSize: '0.85rem', color: 'text.primary',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {c.label}
                      </Typography>
                    </Stack>
                    <Typography sx={{
                      fontSize: '0.78rem', color: 'text.secondary',
                      textTransform: 'capitalize',
                    }}>
                      {c.category}
                    </Typography>
                    <Typography sx={{
                      fontSize: '0.85rem', textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                      color: c.mwh != null && c.mwh > 0 ? 'text.primary' : 'text.disabled',
                    }}>
                      {c.mwh != null && c.mwh > 0 ? fmtMWhCompact(c.mwh) : '—'}
                    </Typography>
                    <Typography sx={{
                      fontSize: '0.88rem', fontWeight: 700, textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                      color: isNeg ? '#ef4444' : 'text.primary',
                    }}>
                      {fmtAUDCompact(c.aud)}
                    </Typography>
                    <Typography sx={{
                      fontSize: '0.85rem', textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                      color: c.aud_per_mwh != null ? 'text.primary' : 'text.disabled',
                    }}>
                      {c.aud_per_mwh != null ? `A$${c.aud_per_mwh.toFixed(2)}` : '—'}
                    </Typography>
                    <Typography sx={{
                      fontSize: '0.78rem', textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums', color: 'text.disabled',
                    }}>
                      {(c.pct || 0).toFixed(1)}%
                    </Typography>
                    {/* Per-row horizontal bar — the "chart" inside the grid.
                        Each row's bar is scaled to the longest in the table
                        so the visual ranking is faithful (the #1 service
                        fills the column, #2 takes its proportional share). */}
                    <Box sx={{
                      height: 14, py: '4px',
                      display: 'flex', alignItems: 'center',
                    }}>
                      <Box sx={{
                        height: 6, width: '100%',
                        bgcolor: colors.track,
                        borderRadius: 1.5, overflow: 'hidden',
                      }}>
                        <Box
                          title={`${c.label}: ${fmtAUDCompact(c.aud)}`}
                          sx={{
                            height: '100%',
                            width: `${Math.min(barPct, 100)}%`,
                            bgcolor: color, opacity: 0.9,
                            transition: 'width 240ms ease',
                          }}
                        />
                      </Box>
                    </Box>
                  </React.Fragment>
                );
              });
            })()}
          </Box>

          {/* The asset-manager scorecard (revenue intensity + Pareto) has
              moved up to the KPI section — see <AssetManagerView /> above
              the dispatch chart in the page body. The Revenue components
              card now stays focused on the analyst view. */}
        </>
      )}
    </Card>
  );
}


export default function CommercialOverview() {
  const { portfolioId } = useParams();
  const colors = useThemeColors();

  // Period — same default as Portfolio Overview (30 days). The user can flip
  // to any preset or a custom range, with full daily/.../yearly frequency.
  const [period, setPeriod] = useState(() => defaultPeriodValue('30d'));

  // Asset scope — default is the first asset (rather than "All assets") so
  // the page lands on a meaningful single-DUID view straight away. We
  // discover the first asset id from /overview on initial load.
  const [assetId, setAssetId] = useState(null);
  const [assets, setAssets]   = useState([]);

  const [data, setData]   = useState(null);
  // Prior-period snapshot — same length window immediately before the
  // current one — so every KPI can carry a Δ chip.
  const [priorData, setPriorData] = useState(null);
  const [dispatchData, setDispatchData] = useState(null);
  const [loading, setLoading]           = useState(true);
  const [dispatchLoading, setDispatchLoading] = useState(true);
  const [error, setError] = useState(null);

  // Battery-specific overlay — fetched only when the active asset is a BESS
  // so the page can swap KPI tiles and split chart for revenue/FCAS stack
  // instead of curtailment cards.
  const [batteryData, setBatteryData] = useState(null);
  const [batteryLoading, setBatteryLoading] = useState(false);

  // Revenue split — PPA + merchant + per-cause losses in dollars, from
  // /commercial/revenue (2026-06-10 METIS review). The PPA share/strike
  // ride along in the payload so the UI can caption the split honestly
  // ("80% PPA · $85.70" comes from the Firestore contract, not a constant).
  const [revenueData, setRevenueData] = useState(null);
  const [revenueLoading, setRevenueLoading] = useState(true);

  const selectedAsset = useMemo(
    () => (assets || []).find((a) => a.asset_id === assetId),
    [assets, assetId]
  );
  const isBattery = selectedAsset?.fuel === 'Battery';

  // Bootstrap the asset list and pick the first DUID as the default scope.
  // Done once; subsequent loads honour the user's selection.
  useEffect(() => {
    let cancelled = false;
    cachedGet(`${API_BASE_URL}/api/portfolios/${portfolioId}/overview`,
              { params: { period: '30d' } })
      .then((r) => {
        if (cancelled) return;
        const list = r.data?.assets || [];
        setAssets(list);
        if (list.length > 0 && !assetId) setAssetId(list[0].asset_id);
      })
      .catch(() => { if (!cancelled) setAssets([]); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioId]);

  // Main overview fetch — fires once we know which asset to scope to.
  async function load(periodValue, scope) {
    if (!scope) return;   // wait for the bootstrap to pick a default
    setLoading(true); setError(null);
    try {
      // Explicit start/end for every preset — the picker is the single
      // source of truth for the window (see utils/periodParams.js).
      // Frequency drives the curtailment-bucket granularity on the backend
      // so the Potential-vs-Realized chart aligns with the picker.
      const params = periodApiParams(periodValue);
      if (scope && scope !== 'all') params.asset_id = scope;
      const r = await cachedGet(
        `${API_BASE_URL}/api/portfolios/${portfolioId}/overview`,
        { params },
      );
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(period, assetId); /* eslint-disable-next-line */ },
            [portfolioId, period, assetId]);

  // Prior-period fetch — same length window immediately before the current
  // one, sent as a custom range. Drives the Δ chips on every KPI tile.
  // The /overview endpoint accepts ?period=custom&start=...&end=...
  useEffect(() => {
    if (!assetId) return;
    const startMs = new Date(period.start).getTime();
    const endMs   = new Date(period.end).getTime();
    const windowMs = endMs - startMs;
    if (!windowMs || windowMs <= 0) return;
    const priorStart = new Date(startMs - windowMs).toISOString();
    const priorEnd   = new Date(startMs).toISOString();
    let cancelled = false;
    const params = {
      period: 'custom',
      start:  priorStart,
      end:    priorEnd,
      frequency: period.frequency,
    };
    if (assetId && assetId !== 'all') params.asset_id = assetId;
    cachedGet(`${API_BASE_URL}/api/portfolios/${portfolioId}/overview`, { params })
      .then((r) => { if (!cancelled) setPriorData(r.data); })
      .catch(() => { if (!cancelled) setPriorData(null); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioId, period, assetId]);

  // Dispatch chart fetch — independent so swapping the chart's asset doesn't
  // refetch the whole overview. Tied to the same scope by default but the
  // dispatch endpoint can also be hit with its own asset_id; here we keep
  // them in lockstep so the whole page stays asset-consistent.
  useEffect(() => {
    if (!assetId) return;
    let cancelled = false;
    setDispatchLoading(true);
    // Explicit window + the picker's frequency so the backend buckets the
    // dispatch series to match (otherwise the chart auto-snapped to hourly
    // for any 30-day window regardless of what the user picked).
    const params = periodApiParams(period);
    if (assetId && assetId !== 'all') params.asset_id = assetId;
    cachedGet(`${API_BASE_URL}/api/portfolios/${portfolioId}/dispatch`,
              { params })
      .then((r) => { if (!cancelled) setDispatchData(r.data); })
      .catch(() => { if (!cancelled) setDispatchData(null); })
      .finally(() => { if (!cancelled) setDispatchLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioId, period, assetId]);

  // Revenue split fetch — generation assets only (batteries have their own
  // revenue stack). Window + frequency mirror the curtailment series so the
  // dollar chart buckets line up with everything else on the page.
  useEffect(() => {
    if (!assetId || isBattery) {
      setRevenueData(null);
      return;
    }
    let cancelled = false;
    setRevenueLoading(true);
    const params = periodApiParams(period);
    params.asset_id = assetId;
    cachedGet(`${API_BASE_URL}/api/portfolios/${portfolioId}/commercial/revenue`,
              { params })
      .then((r) => { if (!cancelled) setRevenueData(r.data); })
      .catch(() => { if (!cancelled) setRevenueData(null); })
      .finally(() => { if (!cancelled) setRevenueLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioId, period, assetId, isBattery]);

  // Battery-specific data — fires only for BESS DUIDs. We piggyback on the
  // existing /api/batteries/<duid>/commercial endpoint, which carries the
  // revenue stack (energy + 10 FCAS markets), daily revenue series, and
  // BESS-flavoured KPIs (RTE, cycles, SoC, etc.).
  // We translate the period picker's window into a `days` count so the
  // time-series chart can actually bucket across enough rows when the user
  // picks 90d / YTD / Custom.
  const periodDays = useMemo(() => {
    if (!period) return 30;
    if (period.code === 'mtd')  return 31;
    if (period.code === '7d')   return 7;
    if (period.code === '30d')  return 30;
    if (period.code === '90d')  return 90;
    if (period.code === 'ytd') {
      const ms = Date.now() - new Date(new Date().getFullYear(), 0, 1).getTime();
      return Math.max(30, Math.round(ms / 86_400_000));
    }
    if (period.code === 'custom' && period.start && period.end) {
      const ms = new Date(period.end).getTime() - new Date(period.start).getTime();
      return Math.max(1, Math.round(ms / 86_400_000));
    }
    return 30;
  }, [period]);

  useEffect(() => {
    if (!isBattery || !assetId) {
      setBatteryData(null);
      return;
    }
    let cancelled = false;
    setBatteryLoading(true);
    cachedGet(`${API_BASE_URL}/api/batteries/${assetId}/commercial`,
              { params: { days: periodDays } })
      .then((r) => { if (!cancelled) setBatteryData(r.data); })
      .catch(() => { if (!cancelled) setBatteryData(null); })
      .finally(() => { if (!cancelled) setBatteryLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, isBattery, periodDays]);

  const k  = data?.kpis      || {};
  const bk = batteryData?.kpis || {};

  // Build a delta-chip descriptor for any pair of numeric metrics.
  // Returns `{ value, label, direction }` shaped for <KpiCard delta=…> or
  // `undefined` if there's no useful comparison (missing prior, zero base).
  // The label is always `vs prior` so the chips read the same everywhere.
  const fmtDelta = (current, prior, opts = {}) => {
    if (current == null || prior == null) return undefined;
    if (prior === 0)                       return undefined;
    const diff = current - prior;
    const pct  = (diff / Math.abs(prior)) * 100;
    const dir  = pct >  0.5 ? 'up'   :
                 pct < -0.5 ? 'down' : 'flat';
    const sign = pct >= 0 ? '+' : '−';
    const val  = opts.pp
      ? `${sign}${Math.abs(pct).toFixed(1)}pp`        // percentage-point delta
      : `${sign}${Math.abs(pct).toFixed(1)}%`;
    return { value: val, label: 'vs prior', direction: dir };
  };
  const curtailment = data?.curtailment || { curtailed_mwh: 0, split: [] };
  const econCurtailMwh  = sumCause(curtailment, 'Neg. price');
  const gridCurtailMwh  = sumCause(curtailment, 'Network');

  // ── Revenue split bar chart data — per-bucket, in dollars ───────────────
  // Series comes from /commercial/revenue, bucketed at the user-selected
  // frequency. The dollar mirror of the Technical production chart
  // (2026-06-10 METIS review): each bucket stacks
  //   ppa_aud         : delivered × contracted share × strike (from the PPA)
  //   merchant_aud    : delivered × merchant share × spot (negative when RRP < 0)
  //   econ_loss_aud   : economic curtailment × blended price
  //   grid_loss_aud   : grid curtailment × blended price
  //   outage_loss_aud : availability-gap energy × blended price
  // so the stack sums to potential_aud (dashed per-bucket marks), with a
  // "regional average" line (delivered × time-avg regional RRP) as benchmark.
  const revenueChart = useMemo(() => {
    const rs = revenueData;
    if (!rs?.series?.length) return null;
    const s = rs.series;
    return {
      xs:         s.map((p) => new Date(p.t)),
      ppa:        s.map((p) => p.ppa_aud || 0),
      merchant:   s.map((p) => p.merchant_aud || 0),
      econLoss:   s.map((p) => p.econ_loss_aud || 0),
      gridLoss:   s.map((p) => p.grid_loss_aud || 0),
      outageLoss: s.map((p) => p.outage_loss_aud || 0),
      potential:  s.map((p) => p.potential_aud || 0),
      benchmark:  s.map((p) => p.benchmark_aud),
      bucketMinutes: rs.bucket_minutes || 5,
    };
  }, [revenueData]);

  // Granularity-aware x-axis label formatter for the revenue chart.
  // timeZone:'UTC' throughout — series timestamps are NEM-clock-as-UTC, so
  // browser-local rendering shifts labels (+1 h UK, +8 SGT, +10 Sydney).
  const revenueTickFmt = (v, ctx) => {
    if (!revenueChart) return '';
    const d = v instanceof Date ? v : new Date(v);
    const m = revenueChart.bucketMinutes;
    const tip = ctx?.location === 'tooltip';
    if (m < 60) {
      return tip
        ? d.toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' })
        : d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });
    }
    if (m < 1440) {
      return tip
        ? d.toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' })
        : d.toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', hour12: false, timeZone: 'UTC' });
    }
    if (m === 1440)  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' });
    if (m === 10080) return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' });
    return d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric', timeZone: 'UTC' });
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
      <CommercialWorkspaceTabs />
      {/* Header — title left, asset + period + refresh right. */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.025em' }}>
          Commercial Performance
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          {assets.length > 1 && (
            <AssetPicker
              value={assetId || ''}
              onChange={setAssetId}
              assets={assets}
              colors={colors}
              allowAll={false}      // page is always single-asset
              minWidth={220}
              placeholder="Select asset"
            />
          )}
          <PeriodPicker value={period} onChange={setPeriod} size="small" />
          <IconButton size="small" onClick={() => load(period, assetId)}
                      sx={{ color: 'text.secondary' }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      {/* Sub-header — period chip + date range + active asset, sourced from
          state so it reflects the user's pick immediately. */}
      {period && (
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5, ml: 0.25 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75,
                     color: 'text.secondary', fontSize: '0.8rem' }}>
            <FiberManualRecordIcon sx={{ fontSize: 8, color: 'primary.main' }} />
            <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {period.label}
            </Box>
            <Box component="span" sx={{ color: 'text.disabled' }}>·</Box>
            <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {fmtDate(period.start)}
              <Box component="span" sx={{ mx: 0.5, color: 'text.disabled' }}>→</Box>
              {fmtDate(period.end)}
            </Box>
            {assetId && (
              <>
                <Box component="span" sx={{ color: 'text.disabled' }}>·</Box>
                <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
                  {(assets.find((a) => a.asset_id === assetId)?.name) || assetId}
                </Box>
              </>
            )}
          </Box>
        </Stack>
      )}

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

      {/* KPI row — left column stacks two money KPIs, right column is a
          donut card. Layout shape is shared between generation and battery
          assets; what fills it swaps based on the selected asset's fuel. */}
      {(() => {
        // ── Generation flavour (Solar / Wind / Other) ───────────────────
        const generationMwh = k.generation_mwh || 0;
        const totalPotential = generationMwh + econCurtailMwh + gridCurtailMwh;
        const genPieData = [
          { id: 'gen',  label: 'Generation',         value: generationMwh,
            color: '#22c55e' },
          { id: 'econ', label: 'Econ. curtailment',  value: econCurtailMwh,
            color: CAUSE_COLOR['Neg. price'] },
          { id: 'grid', label: 'Grid curtailment',   value: gridCurtailMwh,
            color: CAUSE_COLOR['Network'] },
        ].filter((d) => d.value > 0);

        // ── Battery flavour ─────────────────────────────────────────────
        // Bucket the 10-way FCAS stack into 3 head-line categories so the
        // donut stays readable: Energy arbitrage / Raise FCAS / Lower FCAS.
        const components = batteryData?.revenue_stack?.components || [];
        const bucket = (filterFn) =>
          components.filter(filterFn).reduce((acc, c) => acc + (c.aud || 0), 0);
        const energyAud = bk.energy_aud != null
          ? bk.energy_aud
          : bucket((c) => c.category === 'energy');
        const raiseAud  = bucket((c) => c.key?.startsWith('RAISE'));
        const lowerAud  = bucket((c) => c.key?.startsWith('LOWER'));
        const battTotal = (bk.revenue_aud || 0) || (energyAud + raiseAud + lowerAud);
        const battPieData = [
          { id: 'energy', label: 'Energy arbitrage', value: energyAud, color: '#D98A2B' },
          { id: 'raise',  label: 'Raise FCAS',       value: raiseAud,  color: '#E9B065' },
          { id: 'lower',  label: 'Lower FCAS',       value: lowerAud,  color: '#3b82f6' },
        ].filter((d) => d.value > 0);

        // Pick which flavour to render
        const pieData     = isBattery ? battPieData  : genPieData;
        const pieTotal    = isBattery ? battTotal    : totalPotential;
        const isPieLoading = isBattery ? batteryLoading : loading;

        // Donut card markup is shared between battery + generation flavours
        // (only the data + headline labels swap). Extracted into a variable
        // so we can drop it into either layout without duplicating ~150
        // lines of JSX.
        const donutCard = (
          <Card sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.5 }}>
                <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                  {isBattery ? 'Revenue stack' : 'Production split'}
                </Typography>
                <Typography variant="caption" sx={{
                  color: 'text.disabled',
                  fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap',
                }}>
                  {isBattery
                    ? `Total ${fmtAUDCompact(pieTotal)}`
                    : `Potential ${fmtMWhCompact(pieTotal)}`}
                </Typography>
              </Stack>
              {isPieLoading ? (
                <Skeleton variant="rectangular" height={220} sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
              ) : pieTotal <= 0 ? (
                <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.disabled">
                    {isBattery ? 'No revenue data for this battery.' : 'No dispatch data for this asset.'}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{
                  display: 'grid',
                  // Reserve a fixed column for the donut so the legend never
                  // gets squeezed under it on narrow viewports. Gap is wide
                  // enough that the two columns can't visually butt up.
                  gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' },
                  alignItems: 'center',
                  columnGap: 3, rowGap: 2,
                  flexGrow: 1,
                }}>
                  {/* Donut on the left */}
                  <Box sx={{
                    position: 'relative', width: 180, height: 180, mx: 'auto',
                  }}>
                    <PieChart
                      width={180}
                      height={180}
                      margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                      // `hideLegend` is the v8 prop name — `slotProps.legend.hidden`
                      // no longer works and the default legend was leaking onto
                      // the canvas next to our custom one.
                      hideLegend
                      series={[{
                        data: pieData,
                        innerRadius: 58,
                        outerRadius: 84,
                        paddingAngle: 1.5,
                        cornerRadius: 2,
                        highlightScope: { faded: 'global', highlighted: 'item' },
                        faded: { innerRadius: 58, additionalRadius: -2, color: 'gray' },
                        // Tooltip rendering — show "1.2 GWh · 90.9%" (or the
                        // AUD-equivalent for battery view) so the unit is
                        // obvious. The formatter picks MWh vs AUD per fuel.
                        valueFormatter: (item) => {
                          const v = item?.value ?? 0;
                          const pct = pieTotal > 0 ? (v / pieTotal) * 100 : 0;
                          const fmt = isBattery ? fmtAUDCompact : fmtMWhCompact;
                          return `${fmt(v)}  ·  ${pct.toFixed(1)}%`;
                        },
                      }]}
                    />
                    {/* Centre overlay — headline figure. For batteries it's
                        the total revenue (AUD); for generation it's MWh
                        delivered to the grid. */}
                    <Box sx={{
                      position: 'absolute', inset: 0,
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                      pointerEvents: 'none', px: 2,
                    }}>
                      <Typography sx={{
                        fontSize: '1.25rem', fontWeight: 700, lineHeight: 1,
                        fontVariantNumeric: 'tabular-nums', color: 'text.primary',
                        whiteSpace: 'nowrap',
                      }}>
                        {isBattery
                          ? fmtAUDCompact(pieTotal)
                          : fmtMWhCompact(generationMwh)}
                      </Typography>
                      <Typography sx={{
                        fontSize: '0.62rem', color: 'text.disabled',
                        letterSpacing: '0.1em', textTransform: 'uppercase',
                        mt: 0.6, whiteSpace: 'nowrap',
                      }}>
                        {isBattery ? 'Total revenue' : 'Generated'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Legend — 4-column grid keeps the dot · label · value ·
                      percent tight against each other. `auto auto auto auto`
                      sizes everything to its content so the value doesn't
                      float across an auto-fill gap. Justified to the start
                      so the cluster sits left, with the card breathing on
                      the right rather than a hole between label and value. */}
                  <Box sx={{
                    display: 'grid',
                    // Four content-sized columns keep the value tight to its
                    // label; the cluster left-justifies and lets the card
                    // breathe on the right.
                    gridTemplateColumns: 'auto auto auto auto',
                    columnGap: 1.5, rowGap: 1.25,
                    alignItems: 'center',
                    justifyContent: 'start',
                  }}>
                    {(isBattery
                      ? [
                          { key: 'energy', label: 'Energy arbitrage', value: energyAud, color: '#D98A2B' },
                          { key: 'raise',  label: 'Raise FCAS',       value: raiseAud,  color: '#E9B065' },
                          { key: 'lower',  label: 'Lower FCAS',       value: lowerAud,  color: '#3b82f6' },
                        ]
                      : [
                          { key: 'gen',  label: 'Generation',        value: generationMwh, color: '#22c55e' },
                          { key: 'econ', label: 'Econ. curtailment', value: econCurtailMwh, color: CAUSE_COLOR['Neg. price'] },
                          { key: 'grid', label: 'Grid curtailment',  value: gridCurtailMwh, color: CAUSE_COLOR['Network'] },
                        ]
                    ).map((row) => {
                      const pct = pieTotal > 0 ? (row.value / pieTotal) * 100 : 0;
                      const isZero = row.value <= 0;
                      const fmt = isBattery ? fmtAUDCompact : fmtMWhCompact;
                      return (
                        <React.Fragment key={row.key}>
                          {/* col 1: colour dot */}
                          <Box sx={{
                            width: 10, height: 10, borderRadius: '50%',
                            bgcolor: row.color, opacity: isZero ? 0.35 : 1,
                          }} />
                          {/* col 2: label */}
                          <Typography sx={{
                            fontSize: '0.85rem', fontWeight: 500,
                            color: isZero ? 'text.disabled' : 'text.primary',
                            overflow: 'hidden', textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap', minWidth: 0,
                          }}>
                            {row.label}
                          </Typography>
                          {/* col 3: value */}
                          <Typography sx={{
                            fontSize: '0.9rem', fontWeight: 700,
                            fontVariantNumeric: 'tabular-nums',
                            color: isZero ? 'text.disabled' : 'text.primary',
                            whiteSpace: 'nowrap', textAlign: 'right',
                          }}>
                            {fmt(row.value)}
                          </Typography>
                          {/* col 4: percentage (dim) */}
                          <Typography sx={{
                            fontSize: '0.72rem',
                            color: 'text.disabled',
                            fontVariantNumeric: 'tabular-nums',
                            whiteSpace: 'nowrap', textAlign: 'right',
                            minWidth: 40,
                          }}>
                            {pct.toFixed(1)}%
                          </Typography>
                        </React.Fragment>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Card>
        );

        // ── Revenue waterfall — the production waterfall in dollars
        // (2026-06-10 METIS review: "mirror the technical waterfall structure
        // but in dollar terms"). Reads top-to-bottom as a story:
        //   Potential revenue → minus Econ → minus Grid → minus Outages → Actual.
        // Totals (Potential, Actual) anchor from zero; loss columns float
        // between the prior and the new cumulative. Identity (from the
        // backend, blended-price methodology):
        //   Potential − Econ − Grid − Outages = Actual (= PPA + merchant).
        const revenueWaterfallCard = (() => {
          const totals = revenueData?.totals || {};
          const econLossAud   = totals.econ_loss_aud   || 0;
          const gridLossAud   = totals.grid_loss_aud   || 0;
          const outageLossAud = totals.outage_loss_aud || 0;
          const actualAud     = totals.actual_aud      || 0;
          const potentialAud  = totals.potential_aud   || 0;
          const ppaInfo       = revenueData?.ppa;
          const afterEcon   = potentialAud - econLossAud;
          const afterGrid   = afterEcon    - gridLossAud;
          // Build per-step `start` / `decrease` / `end` series. Each series
          // is null outside its own column so the per-series colour lands
          // exactly where it should, and a transparent `spacer` series at
          // the bottom of the stack lifts the floating bars to the right
          // level. Same pattern as the CFADS waterfall.
          // Colours mirror the "Revenue split" chart further down the page
          // (and the Technical production palette) so the same concept reads
          // the same colour everywhere:
          //   Potential   → slate  (#94a3b8)
          //   Econ loss   → red    CAUSE_COLOR['Neg. price']
          //   Grid loss   → blue   CAUSE_COLOR['Network']
          //   Outage loss → dark red (#dc2626)
          //   Actual      → green  (#22c55e)
          // The Outages column renders even at $0 — "no outage losses" is
          // information the review explicitly wanted on this screen.
          const labels = ['Potential', 'Econ. curtailment', 'Grid curtailment', 'Outages', 'Actual'];
          const dataset = [
            // Potential revenue — bar 0 → potential
            { label: labels[0], spacer: 0,         start: potentialAud,
              econDecrease: null, gridDecrease: null, outageDecrease: null, end: null,
              tipDelta: potentialAud, tipCum: potentialAud, kind: 'start' },
            // Econ curtailment loss — bar afterEcon → potential
            { label: labels[1], spacer: afterEcon, start: null,
              econDecrease: econLossAud, gridDecrease: null, outageDecrease: null, end: null,
              tipDelta: -econLossAud, tipCum: afterEcon, kind: 'delta' },
            // Grid curtailment loss — bar afterGrid → afterEcon
            { label: labels[2], spacer: afterGrid, start: null,
              econDecrease: null, gridDecrease: gridLossAud, outageDecrease: null, end: null,
              tipDelta: -gridLossAud, tipCum: afterGrid, kind: 'delta' },
            // Outage loss — bar actual → afterGrid
            { label: labels[3], spacer: actualAud, start: null,
              econDecrease: null, gridDecrease: null, outageDecrease: outageLossAud, end: null,
              tipDelta: -outageLossAud, tipCum: actualAud, kind: 'delta' },
            // Actual (end) — bar 0 → PPA + merchant revenue
            { label: labels[4], spacer: 0, start: null,
              econDecrease: null, gridDecrease: null, outageDecrease: null,
              end: actualAud,
              tipDelta: actualAud, tipCum: actualAud, kind: 'end' },
          ];

          // Custom tooltip slot — replaces the default ChartsTooltip popper.
          // The waterfall has an invisible "spacer" series at the bottom of
          // each stack; with the default tooltip the hover hits the spacer
          // first and renders an empty popper for that area. This component
          // returns null whenever the hovered seriesId is 'spacer', so the
          // empty-tooltip-over-empty-space artefact disappears entirely.
          const WaterfallTooltipBody = () => {
            // `useAxesTooltip` (plural — the v8-recommended hook) fires
            // whenever the pointer is anywhere inside a column's lane, not
            // just on the visible bar. It returns an array (one entry per
            // active axis); we want the first cartesian axis. From there we
            // walk the series items and pick the one with real meaning —
            // skipping the invisible spacer and any null-valued sibling —
            // so the tooltip stays one row.
            const axes = useAxesTooltip();
            if (!axes || !axes.length) return null;
            const tip = axes[0];
            const dataIndex = tip.dataIndex;
            const meaningful = (tip.seriesItems || []).find(
              (item) => item.seriesId !== 'spacer' && item.value != null
            );
            if (!meaningful) return null;
            const row = dataset[dataIndex];
            if (!row) return null;
            const isDelta = row.kind === 'delta';
            const stepAud = isDelta ? Math.abs(row.tipDelta) : row.tipCum;
            const pctOfPotential = potentialAud > 0
              ? (stepAud / potentialAud) * 100
              : 0;
            const primary = isDelta
              ? `−${fmtAUDCompact(Math.abs(row.tipDelta))}`
              : fmtAUDCompact(row.tipCum);
            const secondary = `${pctOfPotential.toFixed(1)}% of potential`;
            return (
              <Box sx={{
                bgcolor: colors.surface,
                border: '1px solid', borderColor: colors.hairline2,
                borderRadius: 1.5,
                boxShadow: '0 6px 18px rgba(0,0,0,0.16)',
                px: 1.5, py: 1, minWidth: 160,
              }}>
                <Stack direction="row" alignItems="center" spacing={0.85} sx={{ mb: 0.4 }}>
                  <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: meaningful.color }} />
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'text.primary' }}>
                    {row.label}
                  </Typography>
                </Stack>
                <Typography sx={{
                  fontSize: '0.95rem', fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                  color: meaningful.color,
                }}>
                  {primary}
                </Typography>
                <Typography sx={{
                  fontSize: '0.7rem', color: 'text.disabled', letterSpacing: '0.04em',
                }}>
                  {secondary}
                </Typography>
              </Box>
            );
          };
          const WaterfallTooltip = (props) => (
            <ChartsTooltipContainer {...props} trigger="axis">
              <WaterfallTooltipBody />
            </ChartsTooltipContainer>
          );

          return (
            <Card sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.5 }}>
                <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                  Revenue
                </Typography>
                {/* Contract-driven split caption — share/strike come from the
                    asset's Firestore PPA, so this is honest, not a constant. */}
                <Typography variant="caption" sx={{
                  color: 'text.disabled', fontVariantNumeric: 'tabular-nums',
                }}>
                  {ppaInfo?.ppa_count > 0
                    ? `${Math.round((ppaInfo.contracted_share || 0) * 100)}% PPA @ A$${(ppaInfo.strike_aud_per_mwh || 0).toFixed(2)} · ${Math.round((1 - (ppaInfo.contracted_share || 0)) * 100)}% merchant`
                    : '100% merchant — no PPA on file'}
                </Typography>
              </Stack>
              {revenueLoading ? (
                <Skeleton variant="rectangular" height={260} sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
              ) : potentialAud <= 0 ? (
                <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
                  <Typography color="text.disabled">No revenue data for this asset.</Typography>
                </Box>
              ) : (
                <Box sx={{ flexGrow: 1 }}>
                  <BarChart
                    height={260}
                    dataset={dataset}
                    xAxis={[{
                      scaleType: 'band',
                      dataKey: 'label',
                      tickLabelStyle: { fontSize: 11, fill: colors.textDim },
                      categoryGapRatio: 0.45,
                    }]}
                    yAxis={[{
                      width: 64,
                      valueFormatter: (v) => fmtAUDCompact(v),
                      tickLabelStyle: { fontSize: 11, fill: colors.textDim },
                    }]}
                    // ORDER MATTERS — spacer first so it sits at the bottom
                    // of the stack and lifts the floating segments. Each
                    // value series has its own colour and only renders at
                    // its own column (nulls elsewhere).
                    series={[
                      { id: 'spacer',         dataKey: 'spacer',         label: '', color: 'transparent', stack: 'wf' },
                      // Potential — slate to match the per-bucket
                      // "Revenue split" chart further down.
                      { id: 'start',          dataKey: 'start',          label: 'Potential',
                        color: '#94a3b8', stack: 'wf' },
                      { id: 'econDecrease',   dataKey: 'econDecrease',   label: 'Econ. curtailment',
                        color: CAUSE_COLOR['Neg. price'], stack: 'wf' },
                      { id: 'gridDecrease',   dataKey: 'gridDecrease',   label: 'Grid curtailment',
                        color: CAUSE_COLOR['Network'], stack: 'wf' },
                      { id: 'outageDecrease', dataKey: 'outageDecrease', label: 'Outages',
                        color: '#dc2626', stack: 'wf' },
                      // Actual — green, same as PPA revenue in the
                      // Revenue split below.
                      { id: 'end',            dataKey: 'end',            label: 'Actual',
                        color: '#22c55e', stack: 'wf' },
                    ]}
                    margin={{ left: 64, right: 16, top: 16, bottom: 30 }}
                    hideLegend
                    slots={{ tooltip: WaterfallTooltip }}
                    slotProps={{
                      tooltip: { trigger: 'axis' },
                    }}
                    sx={{
                      '& .MuiChartsAxis-line': { stroke: colors.hairline2 },
                      '& .MuiChartsAxis-tick':  { stroke: colors.hairline2 },
                      '& .MuiChartsGrid-line':  { stroke: colors.hairline },
                      '& .MuiChartsLegend-label': { fill: colors.textDim, fontSize: '0.75rem' },
                    }}
                  />
                </Box>
              )}
            </Card>
          );
        })();

        // Now compose the page-level KPI row.
        //   • Battery → full-width 3×2 KPI grid above, donut card full-width below.
        //   • Generation → 280px KPI column on the left, donut on the right.
        return isBattery ? (
          // Battery layout: 3×2 KPI grid on the left (six battery-flavoured
          // stats), Revenue stack chart on the right with the remaining
          // width. Revenue itself isn't a card — the chart's centre overlay
          // already shows the total.
          <Box sx={{
            display: 'grid', gap: 2, mb: 3,
            // Wider left column so each KPI card has ~180px — "Capacity factor"
            // fits on a single line and the metric values aren't cramped.
            gridTemplateColumns: { xs: '1fr', lg: '560px 1fr' },
            alignItems: 'stretch',
          }}>
            <Box sx={{
              display: 'grid', gap: 2,
              gridTemplateColumns: 'repeat(3, 1fr)',
              gridAutoRows: '1fr',
            }}>
              <KpiCard
                label="Energy share"
                loading={batteryLoading}
                value={bk.energy_pct != null ? `${bk.energy_pct.toFixed(0)}%` : '—'}
                accent="#D98A2B"
                delta={fmtDelta(bk.energy_pct, priorData?.battery_kpis?.energy_pct, { pp: true })}
                tooltip="Share of revenue from energy arbitrage."
              />
              <KpiCard
                label="FCAS share"
                loading={batteryLoading}
                value={bk.fcas_pct != null ? `${bk.fcas_pct.toFixed(0)}%` : '—'}
                accent="#E9B065"
                delta={fmtDelta(bk.fcas_pct, priorData?.battery_kpis?.fcas_pct, { pp: true })}
                tooltip="Share of revenue from frequency-control ancillary services (10 markets)."
              />
              <KpiCard
                label="RTE"
                loading={batteryLoading}
                value={bk.rte_pct != null ? `${bk.rte_pct.toFixed(1)}%` : '—'}
                accent="#3b82f6"
                tooltip="Round-trip efficiency proxy: discharge MWh / charge MWh."
              />
              <KpiCard
                label="Full cycles"
                loading={batteryLoading}
                value={bk.full_cycles != null ? bk.full_cycles.toFixed(1) : '—'}
                accent="#f59e0b"
                tooltip="Equivalent full cycles: total energy throughput / 2 / nameplate storage."
              />
              <KpiCard
                label="Avg SoC"
                loading={batteryLoading}
                value={bk.avg_soc_pct != null ? `${bk.avg_soc_pct.toFixed(1)}%` : '—'}
                accent="#06b6d4"
                tooltip="Average state-of-charge from DISPATCHLOAD.INITIAL_ENERGY_STORAGE."
              />
              <KpiCard
                label="Capacity factor"
                loading={batteryLoading}
                value={bk.capacity_factor_pct != null ? `${bk.capacity_factor_pct.toFixed(1)}%` : '—'}
                accent="#ec4899"
                tooltip="Discharge MWh / (capacity MW × 24h × days). Energy-out convention."
              />
            </Box>
            {donutCard}
          </Box>
        ) : (() => {
          // ── Generation KPI derivations ─────────────────────────────────
          // "vs prior" Δ chips were dropped from this strip per the
          // 2026-06-10 METIS review ("what does prior even mean — prior
          // month? prior week?"), along with the Capture rate / Capacity
          // factor tiles (capacity factor is a Technical metric; it lives
          // on that screen).
          const capacityMw = selectedAsset?.capacity_mw || 0;

          const revPerMwDay =
            capacityMw > 0 && periodDays > 0 && k.revenue_aud != null
              ? k.revenue_aud / capacityMw / periodDays
              : null;

          // Curtailment + outage value — same blended-price methodology as
          // the revenue waterfall (PPA share × strike + merchant share ×
          // interval RRP), straight from /commercial/revenue so the tile
          // and the charts can never disagree.
          const rTotals = revenueData?.totals || {};
          const lossValue =
            (rTotals.econ_loss_aud || 0) + (rTotals.grid_loss_aud || 0)
            + (rTotals.outage_loss_aud || 0);
          const lossMwh =
            (rTotals.econ_mwh || 0) + (rTotals.grid_mwh || 0)
            + (rTotals.outage_mwh || 0);

          // Region-avg RRP, capture premium, hours-at-neg — server-computed
          // in /dispatch.summary (modules/portfolio/overview.py:_dispatch_summary)
          // so the UI and the AI agent see identical numbers.
          const ds = dispatchData?.summary || {};
          const capturePremium = ds.capture_premium_aud_per_mwh ?? null;
          const negHours       = ds.negative_hours ?? null;
          const negSharePct    = ds.negative_share_pct ?? null;

          return (
            <Box sx={{
              display: 'grid', gap: 2, mb: 3,
              gridTemplateColumns: { xs: '1fr', lg: '11fr 9fr' },
              alignItems: 'stretch',
            }}>
              <Box sx={{
                display: 'grid', gap: 2,
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                gridAutoRows: '1fr',
              }}>
                <KpiCard
                  label="Price captured"
                  loading={loading}
                  value={fmtAUDPerMWh(k.weighted_capture_aud_per_mwh)}
                  unit="/MWh"
                  accent={colors.ACCENT}
                  tooltip="Generation-weighted average dispatch price for the selected period."
                />
                <KpiCard
                  label="Revenue"
                  loading={loading}
                  value={fmtAUDCompact(k.revenue_aud)}
                  accent="#3b82f6"
                  tooltip="Total dispatch revenue (generation × prevailing spot price) for the selected period."
                />
                <KpiCard
                  label="Revenue intensity"
                  loading={loading}
                  value={revPerMwDay != null ? `A$${revPerMwDay.toFixed(0)}` : '—'}
                  unit="/MW/day"
                  accent="#f59e0b"
                  tooltip="LP-grade benchmark: revenue ÷ nameplate capacity ÷ days in window. Lets you compare assets of any size."
                />
                <KpiCard
                  label="Capture premium"
                  loading={loading}
                  value={
                    capturePremium != null
                      ? `${capturePremium >= 0 ? '+' : ''}A$${capturePremium.toFixed(2)}`
                      : '—'
                  }
                  unit="/MWh"
                  accent={
                    capturePremium == null      ? '#E9B065' :
                    capturePremium >= 0          ? '#22c55e' :
                                                   '#ef4444'
                  }
                  tooltip="Asset capture price minus regional time-weighted RRP. Positive = beating the market; negative = duck-curve / shape discount."
                />
                <KpiCard
                  label="Revenue lost"
                  loading={revenueLoading}
                  value={fmtAUDCompact(lossValue)}
                  accent="#ef4444"
                  tooltip={
                    lossMwh > 0
                      ? `Revenue foregone to curtailment + outages — ${fmtMWhCompact(lossMwh)} at the blended PPA/merchant price, interval by interval. ${(rTotals.potential_aud || 0) > 0 ? `${((lossValue / rTotals.potential_aud) * 100).toFixed(1)}% of potential revenue.` : ''}`
                      : 'No curtailment or outage losses in this period.'
                  }
                />
                <KpiCard
                  label="Hours at neg. price"
                  loading={dispatchLoading}
                  value={negHours != null ? `${negHours.toFixed(1)}h` : '—'}
                  unit={negSharePct != null ? `· ${negSharePct.toFixed(1)}%` : ''}
                  accent="#f97316"
                  tooltip="Hours where regional spot RRP went negative — the macro signal for economic curtailment risk."
                />
              </Box>
              {revenueWaterfallCard}
            </Box>
          );
        })();
      })()}

      {/* (RTE / Cycles / Avg SoC / Capacity factor moved into the main KPI
          row's left grid for batteries — no separate strip needed.) */}

      {/* Asset-manager scorecard — sits right under the KPI strip so the
          fund-manager metric ($/MW/day) and the Pareto concentration view
          are visible above the fold, before any analyst-style detail. */}
      {isBattery && (
        <Box sx={{ mb: 3 }}>
          <AssetManagerView
            components={batteryData?.revenue_stack?.components || []}
            capacityMw={batteryData?.asset?.capacity_mw}
            periodDays={batteryData?.period?.days || periodDays}
            colors={colors}
          />
        </Box>
      )}

      {/* Layer 1: Dispatch & spot price chart.
          For batteries we overlay the SoC trace on a third axis so the user
          can see when the asset was charging / discharging / idling against
          spot. */}
      <Card sx={{ p: 3, mb: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>
            {isBattery ? 'Dispatch · SoC · Spot' : 'Dispatch & spot price'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            {isBattery
              ? '5-min · positive MW = discharge, negative = charge'
              : (
                <>
                  Output (left) · {dispatchData?.region || '—'} RRP (right) ·{' '}
                  {(() => {
                    const m = dispatchData?.bucket_minutes;
                    if (!m) return '5-min';
                    if (m < 60) return `${m}-min`;
                    if (m < 1440) return `${m / 60}-hr`;
                    if (m === 1440) return 'daily';
                    if (m === 10080) return 'weekly';
                    return `${m / 60}-hr`;
                  })()}
                </>
              )}
          </Typography>
        </Stack>
        {(isBattery ? batteryLoading : dispatchLoading) ? (
          <Skeleton variant="rectangular" height={320} sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
        ) : (() => {
          // For batteries, swap to the 24h dispatch+SoC payload (canonical BESS
          // view); for generation assets, use the period-driven /dispatch series.
          let xs, mw, rrp, soc, bucketMin;
          if (isBattery) {
            const d = batteryData?.dispatch_24h;
            if (!d?.ts?.length) {
              return (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.disabled">No dispatch data for this battery.</Typography>
                </Box>
              );
            }
            xs  = d.ts.map((t) => new Date(t));
            mw  = d.mw;
            rrp = d.rrp;
            soc = d.soc_mwh;
            bucketMin = 5;          // BESS 24h view is always 5-min
          } else {
            const s = dispatchData?.series || [];
            if (!s.length) {
              return (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.disabled">No dispatch in this period.</Typography>
                </Box>
              );
            }
            xs  = s.map((p) => new Date(p.t));
            mw  = s.map((p) => p.mw);
            rrp = s.map((p) => p.rrp);
            soc = null;
            bucketMin = dispatchData?.bucket_minutes || 5;
          }
          // timeZone:'UTC' throughout — series timestamps are NEM-clock-as-
          // UTC; browser-local rendering shifts labels (+1 h UK, +8 SGT,
          // +10 Sydney). This is the "is the dispatch chart also wrong?"
          // worry from the 2026-06-10 METIS review: the data was right, the
          // labels drifted by the viewer's offset.
          const fmtX = (v, ctx) => {
            const d = v instanceof Date ? v : new Date(v);
            const tip = ctx?.location === 'tooltip';
            if (bucketMin < 60) {
              return tip
                ? d.toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' })
                : d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });
            }
            if (bucketMin < 1440) {
              return tip
                ? d.toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' })
                : d.toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', hour12: false, timeZone: 'UTC' });
            }
            if (bucketMin === 1440) {
              return tip
                ? d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
                : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' });
            }
            if (bucketMin === 10080) {
              return tip
                ? `Week of ${d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })}`
                : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' });
            }
            return d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric', timeZone: 'UTC' });
          };
          // Build axes + series. For batteries we add a third axis ("SoC")
          // on the right so MW (charge/discharge) and SoC stay legible
          // alongside the spot trace.
          const yAxes = isBattery
            ? [
                { id: 'mw',  label: 'MW',        tickLabelStyle: { fill: colors.textDim, fontSize: 11 } },
                { id: 'soc', label: 'SoC (MWh)', tickLabelStyle: { fill: colors.textDim, fontSize: 11 }, position: 'right' },
                { id: 'rrp', label: 'A$/MWh',     tickLabelStyle: { fill: colors.textDim, fontSize: 11 }, position: 'right' },
              ]
            : [
                { id: 'mw',  label: 'MW',    tickLabelStyle: { fill: colors.textDim, fontSize: 11 } },
                { id: 'rrp', label: 'A$/MWh', tickLabelStyle: { fill: colors.textDim, fontSize: 11 }, position: 'right' },
              ];
          const seriesList = [
            { data: mw,  yAxisId: 'mw',  label: isBattery ? 'Cleared MW' : 'Output (MW)',
              color: colors.ACCENT, showMark: false, area: true,
              valueFormatter: (v) => v == null ? '—' : `${v.toFixed(1)} MW` },
            ...(isBattery && soc ? [{
              data: soc, yAxisId: 'soc', label: 'SoC (MWh)', color: '#E9B065',
              showMark: false, connectNulls: true,
              valueFormatter: (v) => v == null ? '—' : `${v.toFixed(0)} MWh`,
            }] : []),
            { data: rrp, yAxisId: 'rrp', label: 'Spot (A$/MWh)',
              color: isBattery ? '#f59e0b' : '#E9B065', showMark: false,
              valueFormatter: (v) => v == null ? '—' : `A$${v.toFixed(2)}` },
          ];
          return (
            <LineChart
              height={320}
              xAxis={[{
                data: xs, scaleType: 'time', valueFormatter: fmtX,
                tickLabelStyle: { fill: colors.textDim, fontSize: 11 },
              }]}
              yAxis={yAxes}
              series={seriesList}
              margin={{ left: 64, right: isBattery ? 104 : 72, top: 32, bottom: 36 }}
              slotProps={{ legend: { position: { vertical: 'top', horizontal: 'right' } } }}
              sx={{
                '& .MuiChartsAxis-line': { stroke: colors.hairline2 },
                '& .MuiChartsAxis-tick':  { stroke: colors.hairline2 },
                '& .MuiChartsGrid-line':  { stroke: colors.hairline },
                '& .MuiAreaElement-root': { fillOpacity: 0.18 },
                '& .MuiChartsLegend-label': { fill: colors.textDim, fontSize: '0.75rem' },
              }}
            />
          );
        })()}
      </Card>

      {/* Layer 2 + 3: Revenue split (and the battery stack) take the full
          width, stacked vertically. */}
      <Box sx={{
        display: 'grid', gap: 2, mb: 3,
        gridTemplateColumns: '1fr',
      }}>
        {/* Second-layer chart — swaps based on fuel.
            • Generation assets → per-bucket Potential vs Realized bar chart
            • Battery assets    → daily revenue stack (energy / raise / lower) */}
        {!isBattery ? (
          <Card sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.5 }}>
              <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                Revenue split
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                {(() => {
                  const m = revenueChart?.bucketMinutes;
                  if (!m) return 'per bucket';
                  if (m < 60)        return `Per ${m}-min`;
                  if (m < 1440)      return `Per ${m / 60}-hr`;
                  if (m === 1440)    return 'Daily';
                  if (m === 10080)   return 'Weekly';
                  if (m === 43200)   return 'Monthly';
                  return `Per ${m}-min`;
                })()}
                {' · PPA + merchant vs revenue lost'}
              </Typography>
            </Stack>
            {revenueLoading ? (
              <Skeleton variant="rectangular" height={320} sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
            ) : !revenueChart ? (
              <Box sx={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.disabled">No revenue data for this asset.</Typography>
              </Box>
            ) : (
              <BarChart
                height={320}
                xAxis={[{
                  data: revenueChart.xs,
                  scaleType: 'band',
                  valueFormatter: revenueTickFmt,
                  tickLabelStyle: { fontSize: 11, fill: colors.textDim },
                  categoryGapRatio: 0.35,
                }]}
                yAxis={[{
                  valueFormatter: (v) => fmtAUDCompact(v),
                  tickLabelStyle: { fontSize: 11, fill: colors.textDim },
                }]}
                series={[
                  // Earned — the PPA/merchant split per the asset's contract
                  // (merchant goes negative in negative-price buckets; the
                  // bar dips below zero, which is the honest picture).
                  { id: 'ppa',      label: 'PPA revenue',  stack: 'realized',
                    data: revenueChart.ppa,
                    color: '#22c55e',
                    valueFormatter: (v) => v == null ? '—' : fmtAUDCompact(v) },
                  { id: 'merchant', label: 'Merchant revenue', stack: 'realized',
                    data: revenueChart.merchant,
                    color: '#E9B065',
                    valueFormatter: (v) => v == null ? '—' : fmtAUDCompact(v) },
                  // Lost — stacked on top so the full bar reaches potential.
                  { id: 'econ',     label: 'Econ. curtailment loss', stack: 'realized',
                    data: revenueChart.econLoss,
                    color: CAUSE_COLOR['Neg. price'],
                    valueFormatter: (v) => v == null ? '—' : fmtAUDCompact(v) },
                  { id: 'grid',     label: 'Grid curtailment loss',  stack: 'realized',
                    data: revenueChart.gridLoss,
                    color: CAUSE_COLOR['Network'],
                    valueFormatter: (v) => v == null ? '—' : fmtAUDCompact(v) },
                  { id: 'outage',   label: 'Outage loss', stack: 'realized',
                    data: revenueChart.outageLoss,
                    color: '#dc2626',
                    valueFormatter: (v) => v == null ? '—' : fmtAUDCompact(v) },
                  // Potential revenue kept as a (hidden) line so the
                  // legend + axis-tooltip still surface it. Visually
                  // it's drawn as dashed per-bucket marks via
                  // <PotentialTicks/> below — matches the discrete
                  // forecast ticks on Facility & Covenants.
                  { id: 'potential', label: 'Potential',
                    type: 'line',
                    data: revenueChart.potential,
                    color: POTENTIAL_COLOR,
                    showMark: false,
                    valueFormatter: (v) => v == null ? '—' : fmtAUDCompact(v) },
                  // Regional average — what the same delivered volume would
                  // have earned at the time-weighted regional RRP. The
                  // "I definitely want to see that regional average" line.
                  { id: 'benchmark', label: 'At regional avg price',
                    type: 'line',
                    data: revenueChart.benchmark,
                    color: '#a78bfa',
                    showMark: false, connectNulls: true,
                    valueFormatter: (v) => v == null ? '—' : fmtAUDCompact(v) },
                ]}
                margin={{ left: 64, right: 24, top: 32, bottom: 44 }}
                slotProps={{
                  legend: { position: { vertical: 'top', horizontal: 'right' } },
                  tooltip: { trigger: 'axis' },
                }}
                sx={{
                  '& .MuiChartsAxis-line': { stroke: colors.hairline2 },
                  '& .MuiChartsAxis-tick':  { stroke: colors.hairline2 },
                  '& .MuiChartsGrid-line':  { stroke: colors.hairline },
                  '& .MuiChartsLegend-label': { fill: colors.textDim, fontSize: '0.75rem' },
                  // Hide the auto-rendered potential line itself —
                  // PotentialTicks is the visual; the LinePlot stays
                  // in the series only for legend + tooltip context.
                  '& .MuiLineElement-series-potential, & .MuiAreaElement-series-potential, & .MuiMarkElement-series-potential': {
                    display: 'none',
                  },
                }}
              >
                <PotentialTicks data={revenueChart.potential} />
              </BarChart>
            )}
          </Card>
        ) : (() => {
          // Battery time-series — Energy + Raise FCAS + Lower FCAS stacked
          // bars, bucketed by the period picker's frequency. The backend
          // returns daily revenue rows; we aggregate them client-side into
          // weekly / monthly / yearly buckets when the user picks a coarser
          // grain. Sub-daily frequencies fall back to daily (battery
          // commercial data is at daily resolution at source).
          const daily = batteryData?.daily_revenue || [];

          // Map period.frequency → bucket key: 'daily' | 'weekly' | 'monthly' | 'yearly'.
          const f = period?.frequency || 'daily';
          const bucketKey =
            f === 'monthly'  ? 'monthly' :
            f === 'yearly'   ? 'yearly'  :
            f === 'weekly'   ? 'weekly'  : 'daily';

          // Build a stable key per bucket for each daily row.
          const bucketIdOf = (d) => {
            const dt = new Date(d.day);
            if (bucketKey === 'yearly')  return `${dt.getFullYear()}`;
            if (bucketKey === 'monthly') return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}`;
            if (bucketKey === 'weekly') {
              // ISO-ish: anchor to the Monday of that week.
              const day = dt.getDay() || 7;
              const monday = new Date(dt); monday.setDate(dt.getDate() - day + 1);
              return monday.toISOString().slice(0, 10);
            }
            return new Date(d.day).toISOString().slice(0, 10);
          };

          const bucketLabelOf = (id) => {
            if (bucketKey === 'yearly')  return id;
            if (bucketKey === 'monthly') {
              const [y, m] = id.split('-');
              return new Date(Number(y), Number(m) - 1, 1)
                .toLocaleDateString('en-AU', { month: 'short', year: '2-digit' });
            }
            const dt = new Date(id);
            if (bucketKey === 'weekly') {
              return `w/${dt.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}`;
            }
            return dt.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
          };

          // Aggregate.
          const map = new Map();
          for (const d of daily) {
            const id = bucketIdOf(d);
            const acc = map.get(id) || { id, energy: 0, raise: 0, lower: 0 };
            acc.energy += d.energy_aud || 0;
            acc.raise  += d.raise_aud  || 0;
            acc.lower  += d.lower_aud  || 0;
            map.set(id, acc);
          }
          const buckets = Array.from(map.values()).sort((a, b) => a.id.localeCompare(b.id));
          const xs    = buckets.map((b) => bucketLabelOf(b.id));
          const ener  = buckets.map((b) => b.energy);
          const raise = buckets.map((b) => b.raise);
          const lower = buckets.map((b) => b.lower);

          const titleSuffix =
            bucketKey === 'yearly'  ? 'per year'  :
            bucketKey === 'monthly' ? 'per month' :
            bucketKey === 'weekly'  ? 'per week'  : 'per day';

          return (
            <Card sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.5 }}>
                <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                  Revenue mix
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                  Energy + FCAS · {titleSuffix}
                </Typography>
              </Stack>
              {batteryLoading ? (
                <Skeleton variant="rectangular" height={320} sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
              ) : !buckets.length ? (
                <Box sx={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.disabled">No revenue for this battery in the period.</Typography>
                </Box>
              ) : (
                <BarChart
                  height={320}
                  xAxis={[{
                    data: xs,
                    scaleType: 'band',
                    tickLabelStyle: { fontSize: 11, fill: colors.textDim },
                    categoryGapRatio: 0.4,
                  }]}
                  yAxis={[{
                    valueFormatter: (v) => fmtAUDCompact(v),
                    tickLabelStyle: { fontSize: 11, fill: colors.textDim },
                  }]}
                  series={[
                    { id: 'energy', label: 'Energy arbitrage', stack: 'rev', data: ener,
                      color: '#D98A2B',
                      valueFormatter: (v) => v == null ? '—' : fmtAUDCompact(v) },
                    { id: 'raise',  label: 'Raise FCAS',       stack: 'rev', data: raise,
                      color: '#E9B065',
                      valueFormatter: (v) => v == null ? '—' : fmtAUDCompact(v) },
                    { id: 'lower',  label: 'Lower FCAS',       stack: 'rev', data: lower,
                      color: '#3b82f6',
                      valueFormatter: (v) => v == null ? '—' : fmtAUDCompact(v) },
                  ]}
                  margin={{ left: 72, right: 24, top: 32, bottom: 44 }}
                  slotProps={{
                    legend: { position: { vertical: 'top', horizontal: 'right' } },
                    tooltip: { trigger: 'axis' },
                  }}
                  sx={{
                    '& .MuiChartsAxis-line': { stroke: colors.hairline2 },
                    '& .MuiChartsAxis-tick':  { stroke: colors.hairline2 },
                    '& .MuiChartsGrid-line':  { stroke: colors.hairline },
                    '& .MuiChartsLegend-label': { fill: colors.textDim, fontSize: '0.75rem' },
                  }}
                />
              )}
            </Card>
          );
        })()}

        {/* Third-layer card — battery only. (The generation-side "Price
            capture vs benchmark" chart moved to the Spot Price & Revenues
            tab per the 2026-06-10 METIS review — anything to do with price
            lives there.)
            Battery → Revenue components horizontal-bar list (full 10-way
            FCAS breakdown). */}
        {isBattery && (
          // Battery revenue stack rendered as the industry-standard pair:
          //   1. 100%-stacked horizontal ribbon  → at-a-glance Energy / Raise / Lower split
          //   2. Sortable component table        → service · category · enablement (MWh)
          //                                       · revenue ($) · $/MWh · share (%)
          // The $/MWh column is what BESS analysts actually use to compare
          // market profitability — revenue alone hides the unit economics.
          <BatteryRevenueStackCard
            components={batteryData?.revenue_stack?.components || []}
            loading={batteryLoading}
            colors={colors}
          />
        )}

      </Box>

      {/* Insights — consolidated "risk & concentration" view that surfaces
          the questions an asset manager asks in the Monday review:
            • Best / Worst single bucket — how lumpy is revenue?
            • Pareto on time buckets    — top X% drive Y% (concentration)
            • Cumulative revenue trend  — pacing vs prior period
            • TB1 / TB2 spreads (battery only) — canonical Modo benchmark */}
      <RevenueInsightsCard
        revenueSeries={data?.revenue_series?.series || []}
        priorRevenueSeries={priorData?.revenue_series?.series || []}
        dispatchSummary={dispatchData?.summary || null}
        isBattery={isBattery}
        capacityMw={selectedAsset?.capacity_mw || 0}
        colors={colors}
        period={period}
        loading={loading}
      />
    </Box>
  );
}
